import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Hint} from '@/components/ui/hint'
import {formatDateOnly} from '@/lib/risk'
import {useBillingCycle, useUpdateBillingCycle, type BillingCycle} from '@/hooks/inci/cartera/use-billing'

/** upgrade-gating-spec §7: el cambio aplica en la siguiente renovación en ambos sentidos.
 * Solo con suscripción activa: sin ella el GET da 404. */
export function CycleToggleSection({businessId, canEdit}: {businessId: string; canEdit: boolean}) {
  const {t} = useTranslation()
  const {data: cycle} = useBillingCycle(businessId, true)
  const update = useUpdateBillingCycle(businessId)

  if (!cycle) return null

  const other: BillingCycle = cycle.billing_cycle === 'mensual' ? 'anual' : 'mensual'
  const pending = cycle.billing_cycle_next && cycle.billing_cycle_next !== cycle.billing_cycle

  return (
    <div className="surface space-y-3 p-5" data-testid="billing-cycle-section">
      <h2 className="font-heading text-base text-foreground">{t('planFacturacion.cicloToggle.title')}</h2>

      {pending ? (
        <div className="flex flex-wrap items-center gap-3" data-testid="billing-cycle-pending">
          <Tag tone="amber">
            {t('planFacturacion.cicloToggle.pending', {
              cycle: t(`planFacturacion.cycle.${cycle.billing_cycle_next}`),
              date: formatDateOnly(cycle.period_end),
            })}
          </Tag>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="billing-cycle-cancel"
              disabled={update.isPending}
              onClick={() => update.mutate(cycle.billing_cycle)}>
              {t('planFacturacion.cicloToggle.cancelPending')}
            </Button>
          )}
          <Hint>{t('planFacturacion.cicloToggle.hint')}</Hint>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-foreground" data-testid="billing-cycle-current">
            {t('planFacturacion.cicloToggle.current', {cycle: t(`planFacturacion.cycle.${cycle.billing_cycle}`)})}
          </span>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              data-testid="billing-cycle-change"
              disabled={update.isPending}
              onClick={() => update.mutate(other)}>
              {t('planFacturacion.cicloToggle.changeTo', {cycle: t(`planFacturacion.cycle.${other}`)})}
            </Button>
          )}
          <Hint>{t('planFacturacion.cicloToggle.hint')}</Hint>
        </div>
      )}
    </div>
  )
}
